/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@compiled/react';
import { RouteComponent, Router, createBrowserHistory } from 'react-resource-router';
import AppProvider from '@atlaskit/app-provider';
import { RelayEnvironmentProvider } from '@atlassian/relay-environment-provider';
import { Root, Main } from '@atlaskit/navigation-system';
import { FlagsProvider } from '@atlaskit/flag';
import { routes } from './routes';

import { initAnalyticsClient, type LoggedInUser } from '@atlassian/twg-microsite-experience';
import { LocaleProvider } from '@atlassian/twg-microsite-experience/locale-provider';
import { NavigationBar } from '@atlassian/twg-microsite-experience/NavigationBar';
import { UserProvider } from '@atlassian/twg-microsite-experience/user-provider';
import { AnalyticsProvider } from '@atlassian/twg-microsite-experience/analytics-provider';
import { PanelProvider } from '@atlassian/twg-microsite-experience/panel-provider';
import { useCallback, useMemo } from 'react';
import { initializeFeatureGates } from '../feature-flags';
import { LazyDebugPanel } from './components/LazyDebugPanel';

// The microsite only registers a single route (`/`), so any other pathname
// (e.g. `/hello`) is rewritten to `/` before the Router subscribes — this
// avoids an extra render pass or loading-state flicker on first mount.
const history = createBrowserHistory();
if (history.location.pathname !== '/') {
	history.replace({ pathname: '/', search: history.location.search, hash: history.location.hash });
}

export const App = (): JSX.Element => {
	const analyticsClient = useMemo(() => initAnalyticsClient(), []);

	const onUserLoginComplete = useCallback(async (user: LoggedInUser) => {
		await initializeFeatureGates(user.accountId, user.orgId);
	}, []);

	return (
		<RelayEnvironmentProvider>
			<AnalyticsProvider analyticsClient={analyticsClient}>
				<LocaleProvider>
					<AppProvider defaultColorMode="dark">
						<FlagsProvider>
							<UserProvider onUserLoginComplete={onUserLoginComplete}>
								<PanelProvider>
									<Router history={history} routes={routes}>
										<Root>
											<Main>
												<NavigationBar />
												<RouteComponent />
											</Main>
											<LazyDebugPanel />
										</Root>
									</Router>
								</PanelProvider>
							</UserProvider>
						</FlagsProvider>
					</AppProvider>
				</LocaleProvider>
			</AnalyticsProvider>
		</RelayEnvironmentProvider>
	);
};
